import { Record, type RecordOf } from 'immutable';

import type { QcsSyncResourceStore } from 'app-components/flux/core/resourceStoreTypes';
import type {
  DetailedQuote,
  QuotesSecurityType,
} from 'app-components/flux/investments-quotes-detailed/investmentsQuotesDetailedTypes';

export const INVESTMENT_HOLDINGS_V2_REDUCER_KEY = 'investmentHoldingsV2Store';
export type INVESTMENT_HOLDINGS_V2_REDUCER_KEY_TYPE = typeof INVESTMENT_HOLDINGS_V2_REDUCER_KEY;

export type InvestmentHoldingV2Type = {
  clientId?: string;
  id?: string;
  createdAt?: string;
  accountId: string;
  quantity?: number | null;
  costBasis?: number | null;
  currentPrice?: number | null;
  currentPriceAt?: string;
  marketValue: number | null;
  isDeleted?: boolean;
  isCash?: boolean;
  localSecurityId?: string;
  securityId?: string;
  securityIdType?: string;
  symbol?: string;
  securityName?: string;
  sharesPerContract?: number | null;
  securityType?: string;
  userCostBasis?: number | null;
  userCostBasisOn?: string;
  quantityWhenUserSetCostBasis?: number | null;
};

export const mkInvestmentHoldingV2: (values?: Partial<InvestmentHoldingV2Type>) => RecordOf<InvestmentHoldingV2Type> =
  Record<InvestmentHoldingV2Type>({
    clientId: undefined,
    id: undefined,
    createdAt: undefined,
    accountId: undefined,
    quantity: 0,
    costBasis: undefined,
    currentPrice: undefined,
    currentPriceAt: undefined,
    marketValue: undefined,
    isDeleted: undefined,
    isCash: undefined,
    localSecurityId: undefined,
    securityId: undefined,
    securityIdType: undefined,
    symbol: undefined,
    securityName: undefined,
    sharesPerContract: undefined,
    securityType: undefined,
    userCostBasis: undefined,
    userCostBasisOn: undefined,
    quantityWhenUserSetCostBasis: undefined,
  } as unknown as InvestmentHoldingV2Type);

export type costBasisStatus = {
  noCostBasis: boolean;
  incompleteCostBasisHoldings?: InvestmentHoldingV2Type[];
};

export type HoldingNodeType = {
  holdings: InvestmentHoldingV2Type[];
  isCash: boolean;
  quotesSecurityType?: QuotesSecurityType;
  key: string;
  localSecurityId: string;
  name?: string;
  price: number;
  priceAsOf?: any;
  quote: any;
  quantity: number;
  shares: number;
  symbol: string;
  todayPercent: number;
  todayValue: number;
  sumOfCostBasis?: number;
  costBasis?: number;
  userCostBasis?: number;
  userCostBasisOn?: string;
  quantityWhenUserSetCostBasis?: number;
  netGain?: number;
  value: number;
  costBasisStatus?: costBasisStatus;
  quoteDetailed?: DetailedQuote;
};

export type PerformanceRow = {
  accountId: string;
  accountName: string;
  color: string;
  id: string;
  oneDayPercent: number;
  specialDayPercent: number;
  status: string;
  statusMessage: string;
  todayBalance: number;
};

export type PerformanceType = {
  accountId: string;
  accountName: string;
  amountChange?: number;
  color: string;
  dailyPerformance: { performance: number; performanceOn: string }[];
  endBalance?: number;
  endOn?: string;
  id: string;
  startBalance?: number;
  startOn?: string;
  status: string;
  todayPerformance: { performance: number; performanceOn: string };
  totalPerformance: number;
};

export type InvestmentSymbolLookupItem = {
  exchange?: string;
  id?: string;
  security?: any;
  symbol: string;
  name: string;
  quantity?: number;
  currentPrice?: number;
};

export type Column = {
  key: string;
  label: string;
  align: 'left' | 'right';
  padding: 'normal' | 'none';
};

export const columns: readonly Column[] = Object.freeze([
  { key: 'symbol', label: 'Symbol', align: 'left', padding: 'normal' },
  { key: 'name', label: 'Name', align: 'left', padding: 'normal' },
  { key: 'shares', label: 'Shares', align: 'right', padding: 'normal' },
  { key: 'price', label: 'Price', align: 'right', padding: 'normal' },
  { key: 'value', label: 'Value', align: 'right', padding: 'normal' },
  { key: 'todayValue', label: '1 Day $', align: 'right', padding: 'normal' },
  { key: 'todayPercent', label: '1 Day %', align: 'right', padding: 'normal' },
  { key: 'action', label: '', align: 'right', padding: 'none' },
  { key: 'details', label: '', align: 'right', padding: 'none' },
]);

export type InvestmentHoldingsV2Store = QcsSyncResourceStore<InvestmentHoldingV2Type>;

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [INVESTMENT_HOLDINGS_V2_REDUCER_KEY]: InvestmentHoldingsV2Store;
  }
}
